/**
 * Backend error code mapping helpers.
 *
 * The backend returns structured error responses with an `error_code` field:
 *   { "error": { "type": "...", "message": "...", "error_code": "...", "detail": null } }
 *
 * This module provides types and a helper to extract the error_code from
 * backend API responses. Components call `t(`errors.${code}`)` with the
 * code returned from `getErrorCode()` to render the translated message.
 * Falls back to "UNKNOWN_ERROR" when no known code is present.
 */

/** Known backend error codes as defined in backend/app/core/error_codes.py */
export type BackendErrorCode =
  | "EMAIL_ALREADY_REGISTERED"
  | "INVALID_CREDENTIALS"
  | "ACCOUNT_SUSPENDED"
  | "TWO_FACTOR_REQUIRED"
  | "TWO_FACTOR_INVALID_CODE"
  | "TWO_FACTOR_NOT_ENABLED"
  | "SESSION_NOT_FOUND"
  | "TOKEN_INVALID_OR_EXPIRED"
  | "TOKEN_ALREADY_USED"
  | "USER_NOT_FOUND"
  | "ORGANIZATION_NOT_FOUND"
  | "ORGANIZATION_NAME_CONFLICT"
  | "ORGANIZATION_SLUG_CONFLICT"
  | "CANNOT_DELETE_LAST_ORGANIZATION"
  | "MEMBERSHIP_NOT_FOUND"
  | "MEMBERSHIP_ALREADY_EXISTS"
  | "CANNOT_REVOKE_LAST_OWNER"
  | "PERMISSION_DENIED"
  | "VALIDATION_ERROR"
  // Admin user panel error codes
  | "USER_ALREADY_EXISTS"
  | "USER_SUSPENDED_NOT_ASSIGNABLE"
  | "CANNOT_IMPERSONATE_ADMIN"
  | "CANNOT_IMPERSONATE_SELF"
  | "IMPERSONATION_NOT_ACTIVE"
  | "EXPORT_TOO_LARGE"
  | "BULK_LIMIT_EXCEEDED"
  | "INVITE_TOKEN_EXPIRED"
  | "INVITE_TOKEN_INVALID"
  | "PATIENT_PHONE_DUPLICATE"
  | "APPOINTMENT_TERMINAL_STATUS"
  | "APPOINTMENT_TIME_CHANGE_TEMPLATE_MISSING"
  | "APPOINTMENT_TIME_CHANGE_PATIENT_PHONE_MISSING"
  | "APPOINTMENT_TIME_CHANGE_NOTIFICATION_FAILED"
  | "UNKNOWN_ERROR";

/**
 * Shape of the new structured error envelope returned by the backend
 * after the i18n-infrastructure feature ships.
 */
export interface BackendErrorEnvelope {
  error?: {
    type?: string;
    message?: string;
    error_code?: string;
    detail?: unknown;
  };
  /** Legacy: some endpoints still return a flat `detail` string. */
  detail?: string;
}

const KNOWN_CODES = new Set<BackendErrorCode>([
  "EMAIL_ALREADY_REGISTERED",
  "INVALID_CREDENTIALS",
  "ACCOUNT_SUSPENDED",
  "TWO_FACTOR_REQUIRED",
  "TWO_FACTOR_INVALID_CODE",
  "TWO_FACTOR_NOT_ENABLED",
  "SESSION_NOT_FOUND",
  "TOKEN_INVALID_OR_EXPIRED",
  "TOKEN_ALREADY_USED",
  "USER_NOT_FOUND",
  "ORGANIZATION_NOT_FOUND",
  "ORGANIZATION_NAME_CONFLICT",
  "ORGANIZATION_SLUG_CONFLICT",
  "CANNOT_DELETE_LAST_ORGANIZATION",
  "MEMBERSHIP_NOT_FOUND",
  "MEMBERSHIP_ALREADY_EXISTS",
  "CANNOT_REVOKE_LAST_OWNER",
  "PERMISSION_DENIED",
  "VALIDATION_ERROR",
  // Admin user panel error codes
  "USER_ALREADY_EXISTS",
  "USER_SUSPENDED_NOT_ASSIGNABLE",
  "CANNOT_IMPERSONATE_ADMIN",
  "CANNOT_IMPERSONATE_SELF",
  "IMPERSONATION_NOT_ACTIVE",
  "EXPORT_TOO_LARGE",
  "BULK_LIMIT_EXCEEDED",
  "INVITE_TOKEN_EXPIRED",
  "INVITE_TOKEN_INVALID",
  "PATIENT_PHONE_DUPLICATE",
  "APPOINTMENT_TERMINAL_STATUS",
  "APPOINTMENT_TIME_CHANGE_TEMPLATE_MISSING",
  "APPOINTMENT_TIME_CHANGE_PATIENT_PHONE_MISSING",
  "APPOINTMENT_TIME_CHANGE_NOTIFICATION_FAILED",
  "UNKNOWN_ERROR",
]);

/**
 * Extracts the `error_code` from a backend error response body.
 *
 * Handles both the new structured envelope (`body.error.error_code`) and
 * legacy flat responses (`body.detail`). Returns "UNKNOWN_ERROR" when no
 * recognisable code is found.
 *
 * Usage in a component:
 * ```ts
 * const body = await res.json().catch(() => ({})) as BackendErrorEnvelope;
 * const code = getErrorCode(body);
 * setApiError(t(`errors.${code}`));
 * ```
 */
export function getErrorCode(body: BackendErrorEnvelope): BackendErrorCode {
  const code = body?.error?.error_code;
  if (typeof code === "string" && KNOWN_CODES.has(code as BackendErrorCode)) {
    return code as BackendErrorCode;
  }
  return "UNKNOWN_ERROR";
}

// ---------------------------------------------------------------------------
// El texto de un fallo en pantalla (feature 028, F4f; aquí desde el 2026-10-01)
// ---------------------------------------------------------------------------
//
// Nunca el `message` del servidor: llega en el idioma del backend, a veces en
// inglés («An organization with this name already exists.»), y con su jerga. Si
// el fallo trae un código con texto propio en `errors.*`, ese texto; si no, el
// genérico de la pantalla («No se ha podido guardar. Inténtalo de nuevo.»).
//
// El código lo traen los errores de `lib/*` que lo leen del sobre de error
// (`BillingApiError`, `ScheduleApiError`, `VoiceApiError`…, en `code`).
// `ClinicApiError` no lo trae: con él, siempre el genérico. Un fallo de red
// (`fetch` lanza `TypeError`) tampoco. Sin React: el `t` de
// `useTranslations("errors")` se pasa desde la pantalla. Complementa a
// `getErrorCode`, que lee el sobre crudo.

/** El `t` de `useTranslations("errors")`: lo justo para saber si hay texto y leerlo. */
export interface TraductorErrores {
  (clave: string): string;
  has: (clave: string) => boolean;
}

/** `PLACE_CLAIM_LIMIT`, `SCHEDULE_TIMEZONE_INVALID`…: la forma de un `error_code` del backend. */
const FORMA_CODIGO = /^[A-Z][A-Z0-9_]+$/;

function codigoDe(caught: unknown): string | null {
  if (typeof caught !== "object" || caught === null || !("code" in caught)) return null;
  const codigo = (caught as { code: unknown }).code;
  return typeof codigo === "string" && FORMA_CODIGO.test(codigo) ? codigo : null;
}

export function mensajeDeError(caught: unknown, generico: string, tErrores?: TraductorErrores): string {
  const codigo = codigoDe(caught);
  return codigo && tErrores?.has(codigo) ? tErrores(codigo) : generico;
}
